import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PullToRefresh from "react-simple-pull-to-refresh";
import SearchIcon from "assets/icons/search-icon.svg";
import LeftArrow from "assets/icons/left-arrow.svg";
import { MainLayout } from "layouts";
import {
  PopdownFilter,
  Tabs,
  FilterCard,
  BottomBranchSelect,
} from "components/common";
import { QnaCard } from "components/all/qna";
import Section from "components/ui/Section";
import Text from "components/ui/Text";
import PullToRefreshing from "components/common/PullToRefreshing";
import {
  setResetState,
  setFromTop,
  fetchQnaList,
  setSearchCount,
  setSearchOption,
} from "redux/slices/qna/qnaSlice";
import { getCalendar } from "utils/date";
import Error from "layouts/Error";

const TAB_OPTIONS = [
  { tabKey: 0, value: "ALL", label: "전체" },
  { tabKey: 1, value: "WAITING", label: "대기" },
  { tabKey: 2, value: "DONE", label: "완료" },
];

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
const initialFilterValues = {
  qnaStatus: 0,
  isAdd: false,
  startDt: getCalendar(firstDay),
  endDt: getCalendar(today),
  coachName: "전체 강사",
  seqPartnerCoach: "",
  period: "CURRENT_MONTH",
  searchTerm: "",
  page: 1,
};

const QNARequests = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageRef = useRef();
  const scrollRef = useRef(0);
  const [visible, setVisible] = useState(false);
  const {
    isLoading,
    isMoreData,
    qnaList,
    fromTop,
    activeTab,
    totalQnACount,
    waitQnACount,
    completeQnACount,
    searchCount,
    startDt,
    endDt,
    coachName,
    seqPartnerCoach,
    period,
    searchTerm,
    page,
    isError,
  } = useSelector((state) => state.qna);
  const { seqPartner, seqPartnerBranch, branchUseYn, partnerList } =
    useSelector((state) => state.partner);
  const [seqPartnerBranchForSearch, setSeqPartnerBranchForSearch] =
    useState(seqPartnerBranch);

  useEffect(() => {
    if (fromTop > 0) {
      pageRef.current.scrollTo({
        top: fromTop,
      });
    }
    if (qnaList.length === 0) {
      dispatch(
        fetchQnaList({
          isAdd: false,
          seqPartner,
          seqPartnerBranch: seqPartnerBranchForSearch,
          qnaStatus: activeTab,
          startDt,
          endDt,
          coachName,
          seqPartnerCoach,
          period,
          searchTerm,
          page,
        })
      );
    }
  }, [seqPartnerBranchForSearch]);

  const handleClickTab = (active) => {
    if (activeTab === active) {
      return;
    }
    dispatch(
      fetchQnaList({
        isAdd: false,
        seqPartner,
        seqPartnerBranch: seqPartnerBranchForSearch,
        qnaStatus: active,
        startDt,
        endDt,
        coachName,
        seqPartnerCoach,
        period,
        searchTerm,
        page: 1,
      })
    );
  };

  const handleClickSearch = (values) => {
    dispatch(
      setSearchOption({
        ...values,
        coachName: values.coachInfo.coachName,
        seqPartnerCoach: values.coachInfo.seqPartnerCoach,
      })
    );
    dispatch(
      fetchQnaList({
        isAdd: false,
        seqPartner,
        seqPartnerBranch: seqPartnerBranchForSearch,
        qnaStatus: activeTab,
        ...values,
        coachName: values.coachInfo.coachName,
        seqPartnerCoach: values.coachInfo.seqPartnerCoach,
        page: 1,
      })
    );
  };

  const resetFilter = () => {
    dispatch(setSearchCount(0));
    dispatch(setResetState());
    dispatch(
      fetchQnaList({
        ...initialFilterValues,
        seqPartner,
        seqPartnerBranch: seqPartnerBranchForSearch,
      })
    );
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    scrollRef.current = scrollTop;
    if (scrollTop + clientHeight + 200 > scrollHeight) {
      if (!isLoading && isMoreData && scrollTop !== 0) {
        dispatch(
          fetchQnaList({
            isAdd: true,
            seqPartner,
            seqPartnerBranch: seqPartnerBranchForSearch,
            qnaStatus: activeTab,
            startDt,
            endDt,
            coachName,
            seqPartnerCoach,
            period,
            searchTerm,
            page,
          })
        );
      }
    }
  };

  const handleClickBack = () => {
    dispatch(setResetState());
    navigate(-1);
  };

  const getTabCounts = () => {
    return branchUseYn === "Y" && seqPartnerBranchForSearch === 0
      ? {
          ALL: 0,
          WAITING: 0,
          DONE: 0,
        }
      : {
          ALL: totalQnACount,
          WAITING: waitQnACount,
          DONE: completeQnACount,
        };
  };

  const getInfoMessage = () => {
    if (searchCount > 0) {
      if (!isLoading && !qnaList.length) {
        return (
          <EmptyBox>
            <Text color="bluegray40" lineHeight="24px">
              검색 결과가 없습니다
            </Text>
          </EmptyBox>
        );
      }
    } else {
      if (!isLoading && !qnaList.length) {
        return (
          <EmptyBox>
            <Text color="bluegray40" lineHeight="24px">
              문의사항이 없습니다
            </Text>
          </EmptyBox>
        );
      }
    }
  };

  const isShowBottomBranch = useMemo(() => {
    if (branchUseYn === "Y" && seqPartnerBranch === 0) {
      return true;
    }
    return false;
  }, [branchUseYn, seqPartnerBranch]);

  const rightComponent = () => {
    return (
      <Icon onClick={() => setVisible(true)}>
        <img src={SearchIcon} />
      </Icon>
    );
  };
  const bottomComponent = () => {
    return (
      <>
        {searchCount > 0 && (
          <FilterCard
            initialFilterValues={initialFilterValues}
            filter={{
              startDt,
              endDt,
              coachName,
              seqPartnerCoach,
              searchTerm,
            }}
            searchCount={searchCount}
            setFilter={handleClickSearch}
            resetFilter={resetFilter}
          />
        )}
        <Tabs
          activeTab={activeTab}
          setActiveTab={handleClickTab}
          counts={getTabCounts()}
          options={TAB_OPTIONS}
        />
      </>
    );
  };

  const handleRefresh = () => {
    return new Promise((res) => {
      setTimeout(() => {
        dispatch(
          fetchQnaList({
            isAdd: false,
            seqPartner,
            seqPartnerBranch: seqPartnerBranchForSearch,
            qnaStatus: activeTab,
            startDt,
            endDt,
            coachName,
            seqPartnerCoach,
            period,
            searchTerm,
            page: 1,
          })
        ).then(() => {
          res();
        });
      }, 700);
    });
  };

  if (isError) {
    return (
      <MainLayout
        withBottomTab={false}
        withHeader={true}
        backgroundColor="white"
        headerProps={{
          padding: "0 20px 0 0", // right padding
          withBackButton: true,
          centerTitle: "문의사항 관리",
          rightComponent: () => (
            <Icon onClick={() => {}}>
              <img src={SearchIcon} />
            </Icon>
          ),
          bottomComponent: bottomComponent,
          withBackButton: false,
          leftIcon: LeftArrow,
          onClickLeftIcon: handleClickBack,
        }}
      >
        <Error mode="section" onRefresh={handleRefresh} />
      </MainLayout>
    );
  }

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      resistance={2}
      pullingContent={<></>}
      refreshingContent={<PullToRefreshing />}
    >
      <MainLayout
        withBottomTab={false}
        withHeader={true}
        backgroundColor="white"
        headerProps={{
          padding: "0 20px 0 0", // right padding
          withBackButton: true,
          centerTitle: "문의사항 관리",
          rightComponent: rightComponent,
          bottomComponent: bottomComponent,
          withBackButton: false,
          leftIcon: LeftArrow,
          onClickLeftIcon: handleClickBack,
        }}
      >
        {isShowBottomBranch && seqPartnerBranchForSearch === 0 ? (
          <Message>
            <Text lineHeight="28px" fontSize="18px" color="#6B748B">
              지점별로 확인할 수 있어요.
            </Text>
            <Text lineHeight="24px" color="#949DB2">
              조회하고자 하는 지점을 선택해 주세요.
            </Text>
          </Message>
        ) : (
          <Wrapper searchCount={searchCount}>
            <ListWrapper
              ref={pageRef}
              onScroll={handleScroll}
              isShowBottomBranch={isShowBottomBranch}
            >
              {getInfoMessage()}
              {qnaList.length !== 0 && (
                <>
                  <ListCard>
                    {qnaList.map((v) => (
                      <QnaCard
                        key={v.uploadId}
                        title={v.uploadName}
                        userName={v.memberName}
                        date={v.regDt}
                        qnaDone={v.answerCompleteYn}
                        onClick={() => {
                          dispatch(setFromTop(scrollRef.current));
                          navigate(`${v.uploadId}`, {
                            state: {
                              title: v.uploadName,
                              userName: v.memberName,
                              date: v.regDt,
                              qustionData: v.uploadContent,
                            },
                          });
                        }}
                      />
                    ))}
                  </ListCard>
                  {period === "CURRENT_MONTH" && (
                    <ListTextInfo>
                      <Text color="bluegray40" lineHeight="24px">
                        이번 달 문의 내역을 조회했습니다
                        <br />
                        이전 내역을 보려면 원하는 기간으로 검색해주세요
                      </Text>
                    </ListTextInfo>
                  )}
                </>
              )}
              {isLoading && (
                <Wating>
                  <Section isLoading />
                </Wating>
              )}
            </ListWrapper>
          </Wrapper>
        )}

        <BottomBranchSelect
          searchBranch={seqPartnerBranch}
          onChange={(branch) => setSeqPartnerBranchForSearch(branch)}
        />
        <PopdownFilter
          visible={visible}
          onClose={setVisible}
          filter={{
            startDt,
            endDt,
            period,
            coachInfo: { coachName, seqPartnerCoach },
            searchTerm,
          }}
          setFilter={handleClickSearch}
          setSearchCount={() => dispatch(setSearchCount(1 + searchCount))}
          filterType="coach"
        />
      </MainLayout>
    </PullToRefresh>
  );
};

const Wrapper = styled.div`
  margin-top: ${(props) => (props.searchCount > 0 ? "108px" : "52px")};
  height: calc(
    100% - ${(props) => (props.searchCount > 0 ? "170px" : "112px")}
  );
`;

const ListWrapper = styled.div`
  width: 100%;
  padding: 0 20px;
  height: calc(
    100% - ${(props) => (props.isShowBottomBranch ? "116px" : "0px")}
  );
  /* height: 100%; */

  overflow: scroll;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const ListCard = styled.div``;
const ListTextInfo = styled.div`
  text-align: center;
  margin: 16px 0;
`;

const Message = styled.div`
  width: 100%;
  position: absolute;
  top: 45%;
  text-align: center;
`;

const EmptyBox = styled.div`
  height: calc(100% - 112px);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  outline: none;
  position: relative;
  &:hover {
    cursor: pointer;
  }
`;
const Wating = styled.div`
  margin: 10px;
`;

export default QNARequests;
